import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'

import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import InputAdornment from '@material-ui/core/InputAdornment'
import Chip from '@material-ui/core/Chip'
import Alert from '@material-ui/lab/Alert'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { withStyles } from '@material-ui/core/styles'

import { KeyboardDatePicker } from '@material-ui/pickers'
import { format, parse, subDays, addDays } from 'date-fns'
import { useParcelStatistics } from '../../../../../adapters/sentinel'

import FertilizerMapKmean from './FertilizerMapKmean'
import { MaxCloudCoverageSelector } from '../../MaxCloudCoverageSelector'

import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Button from '@material-ui/core/Button'
import Slider from '@material-ui/core/Slider'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import SelectedImagesList from './SelectedImagesList'

import {
	computeColorLabels,
	findClosestLabel,
} from '../../../../../utils/gradient'
import { handleDownloadShapefile } from '../../../../../adapters/api-client'
import { useTransferToJohnDeereOC } from '../../../../../adapters/johndeereoc'
import { useAuth } from '../../../../../context/AuthContext'
import { usePaletteScript } from '../../../../../adapters/paletteRange'
import { usePaletteAdjustmentsEnabled } from '../../../../../context/PaletteAdjustmentsContext'
import { rescaleLegend } from '../../../../../utils/palette'
import GraniotPremiumButton from '@graniot/common/utils/button'
import { getLayerWithPriority } from '@graniot/common/utils'

import { find, uniq } from 'lodash'

const useStyles = makeStyles((theme) => ({
	imgParcel: {
		maxHeight: 400,
		padding: theme.spacing(1),
	},
	imgContainer: {
		minHeight: 400,
	},
	divider: {
		margin: theme.spacing(2),
	},
	pickers: {
		marginTop: theme.spacing(2),
	},
	loading: {
		marginBottom: theme.spacing(20),
		marginTop: theme.spacing(20),
	},
	small: {
		width: theme.spacing(4),
		marginLeft: theme.spacing(0.5),
		//height: theme.spacing(4),
		[theme.breakpoints.down('sm')]: {
			width: theme.spacing(3),
			//height: theme.spacing(3),
		},
	},
	fixGridSwitch: {
		marginLeft: theme.spacing(1.5),
	},
}))

const borderComputerTypes = [
	{
		name: 'John Deere',
	},
	{
		name: 'Amazone',
	},
	{
		name: 'Precision Planting 20|20',
	},
	{
		name: 'Trimble',
	},
	{
		name: 'Shapefile',
	},
	{
		name: 'KML',
	},
	{
		name: 'ISOXML',
	},
	{
		name: 'PDF Report',
	},
]

const AntSwitch = withStyles((theme) => ({
	root: {
		width: 28,
		height: 16,
		padding: 0,
		display: 'flex',
		marginLeft: theme.spacing(0.5),
		marginRight: theme.spacing(1),
	},
	switchBase: {
		padding: 2,
		color: theme.palette.common.white,
		'&$checked': {
			transform: 'translateX(12px)',
			color: theme.palette.common.white,
			'& + $track': {
				opacity: 1,
				backgroundColor: theme.palette.primary.main,
				borderColor: theme.palette.primary.main,
			},
		},
	},
	thumb: {
		width: 12,
		height: 12,
		boxShadow: 'none',
	},
	track: {
		border: `1px solid ${theme.palette.grey[500]}`,
		borderRadius: 16 / 2,
		opacity: 1,
		backgroundColor: theme.palette.primary.main,
	},
	checked: {},
}))(Switch)

export function zonesReducer(state, action) {
	if (action === undefined) return

	switch (action.type) {
		case 'rate':
			return {
				...state,
				features: state.features.map((zone) => {
					if (zone.id === action.value.zone)
						return {
							...zone,
							properties: {
								...zone.properties,
								rate: action.value.rate,
							},
						}
					return zone
				}),
			}
		case 'seed':
			return { ...action.value }
		case 'reset':
			return null
		default:
			throw new Error()
	}
}

export function renderImageListReducer(state, action) {
	switch (action.type) {
		case 'ADD': {
			return state.concat([action.payload])
		}
		case 'REMOVE': {
			return state.filter((x) => x.date !== action.payload.date)
		}
		case 'RESET': {
			return []
		}
		default:
			return state
	}
}

function FertilizerVegetation({
	layers,
	parcel,
	selectedResolution,
	width,
	farms,
}) {
	const classes = useStyles()
	const { t } = useTranslation()
	const { loggedUser, isClientPaying } = useAuth()
	const [selectedLayer, setSelectedLayer] = React.useState(getLayerWithPriority(layers, 'NDVI'))
	const [boderComputer, setBoderComputer] = React.useState({
		name: 'John Deere',
	})
	const [maxCC, setMaxCC] = React.useState(['10x10 meters', '3x3 meters'].includes(selectedResolution.resolution) ? 0.9 : 1)
	const [zones, setZones] = React.useState(3)
	const [zonedGeom, setZonedGeom] = React.useReducer(zonesReducer, null)
	const [errorRate, setErrorRate] = React.useState(false)
	const [startVegetation, setStartVegetation] = React.useState({
		image: null,
		date: null,
	})
	const [imagesDate, dispatchRenderImage] = React.useReducer(
		renderImageListReducer,
		[]
	)

	const [fromDate, setFromDate] = React.useState(
		`01/01/${
			selectedResolution.id === 1
				? 2017
				: selectedResolution.id === 2
				? 2019
				: 2022
		}`
	)

	const [baseMap, setBaseMap] = React.useState(null)

	const [featureStats, setFeatureStats] = React.useState(null)

	const [toDate, setToDate] = React.useState(format(new Date(), 'yyyy-LL-dd'))

	const { isLoading, data, refetch, error: fertilizerError } = useParcelStatistics(
		parcel?.id,
		selectedLayer.key,
		maxCC,
		fromDate,
		toDate
	)
	const transferToJohnDeereOC = useTransferToJohnDeereOC()

	// Zone chips are coloured from the legend: follow the user's saved palette range for
	// this farm/layer, like the zoning map (only in apps that enable it)
	const paletteEnabled = usePaletteAdjustmentsEnabled()
	const { data: palette } = usePaletteScript(
		{ parcelId: parcel?.id, layerKey: selectedLayer?.key, rangesOnly: true },
		{ enabled: paletteEnabled }
	)
	const zoneColorLabels = React.useMemo(() => {
		const hasCustomRange = palette?.min != null && palette?.max != null
		const legend = hasCustomRange
			? rescaleLegend(selectedLayer.legend, palette)
			: selectedLayer.legend
		return legend?.length ? computeColorLabels(legend) : []
	}, [selectedLayer, palette?.min, palette?.max])

	React.useEffect(() => {
		if (fromDate) {
			dispatchRenderImage({ type: 'RESET' })
			refetch()
		}
	}, [selectedLayer, maxCC, parcel])

	React.useEffect(() => {
		if (data) {
			let feature_stats = Object.assign([], data)
			feature_stats.reverse()
			setFeatureStats(feature_stats)
		}
	}, [data])

	React.useEffect(() => {
		if (featureStats && featureStats.length > 0) {
			if (featureStats.length > 1) {
				if (
					startVegetation?.date &&
					find(featureStats, { date: startVegetation.date })
				)
					setStartVegetation(
						find(featureStats, { date: startVegetation.date })
					)
				else setStartVegetation(featureStats[featureStats.length - 1])
			} else {
				setStartVegetation(featureStats[0])
			}
		}
	}, [featureStats])

	React.useEffect(() => {
		if (startVegetation?.date) {
			dispatchRenderImage({ type: 'ADD', payload: startVegetation })
		}
	}, [startVegetation])

	const handleCloudCoverageChange = (event, newValue) => {
		setMaxCC(newValue)
	}

	const handleZonesChange = (event, newValue) => {
		setZones(newValue)
	}

	const handleSelectedLayer = (event, newValue) => {
		setSelectedLayer(newValue)
	}

	const handleSelectedFormat = (event, newValue) => {
		setBoderComputer(newValue)
	}

	function indexOfDate2(date) {
		return featureStats.findIndex((d) => {
			return new Date(d.date) - parse(date, 'dd/MM/yyyy', new Date()) > 0
		})
	}

	function indexOfDate(date) {
		return featureStats
			.map((d) => format(new Date(d.date), 'dd/MM/yyyy'))
			.indexOf(date)
	}

	const handleChangeStartVegetation = (comparedDate) => {
		setStartVegetation(featureStats[indexOfDate2(comparedDate)])
	}

	function disableNotAvalibleSentinelImages(date) {
		return indexOfDate(date) === -1
	}

	function getTotalFertilizer() {
		return zonedGeom?.features.reduce(
			(a, geom) => geom.properties.area * geom.properties.rate + a,
			0
		)
	}

	React.useEffect(() => {
		if (getTotalFertilizer() > 0 && errorRate) {
			setErrorRate(false)
		}
	}, [zonedGeom])

	function createShapefile() {
		if (getTotalFertilizer() > 0)
			handleDownloadShapefile({
				zoned_geom: zonedGeom,
				format: boderComputer.name,
				file_name: `${parcel.properties.name}_${startVegetation.date}`,
				parcel_name: parcel.properties.name,
				parcel_area_m2: (parcel.properties.area_ha ?? 0) * 10000,
				parcel_boundary: parcel,
				product_name: 'Variable rate product',
				unit: metric === 'kg' ? 'kg' : 'l',
			})
		else setErrorRate(true)
	}

	function uploadToJohnDeereOC() {
		if (getTotalFertilizer() > 0)
			transferToJohnDeereOC.mutate({
				zoned_geom: zonedGeom,
				format: boderComputer.name,
				file_name: `${parcel.properties.name}_${startVegetation.date}`,
				parcel_name: parcel.properties.name,
				parcel_area_m2: (parcel.properties.area_ha ?? 0) * 10000,
				parcel_boundary: parcel,
				product_name: 'Variable rate product',
				unit: metric === 'kg' ? 'kg' : 'l',
			})
		else setErrorRate(true)
	}

	const [metric, setMetric] = React.useState('kg')

	const handleMetricChange = (e) => {
		e.target.checked ? setMetric('kg') : setMetric('l')
	}

	if (fertilizerError?.error_code === 'PARCEL_EXCEEDS_STATS_LIMIT') {
		return (<Alert severity="warning">{t('parcel_exceeds_stats_limit')}</Alert>)
	}

	return (
		<Grid container spacing={4}>
			<Grid item xs={12} sm={6}>
				<Grid
					container
					direction="row"
					style={{ marginBottom: 20 }}
					spacing={3}
				>
          {['10x10 meters', '3x3 meters'].includes(selectedResolution.resolution) && <Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<MaxCloudCoverageSelector
								maxCC={maxCC}
								handleCloudCoverageChange={
									handleCloudCoverageChange
								}
								info={true}
								width="100%"
							/>
						</Box>
					</Grid>}

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'end' : 'center'
							}
						>
							{startVegetation && (
								<KeyboardDatePicker
									name="base_date"
									label={t('Vegetation date')}
									value={startVegetation.date}
									onChange={(date) =>
										handleChangeStartVegetation(
											format(date, 'dd/MM/yyyy')
										)
									}
									shouldDisableDate={(date) =>
										disableNotAvalibleSentinelImages(
											format(date, 'dd/MM/yyyy')
										)
									}
									maxDate={new Date()}
									minDate={new Date(fromDate)}
									format="dd/MM/yyyy"
									variant="inline"
									size="small"
									fullWidth
									autoOk
								/>
							)}
						</Box>
					</Grid>
					<Grid item xs={12}>
						<SelectedImagesList
							parcel={parcel}
							selectedIndexLayer={selectedLayer}
							imagesDate={imagesDate}
							selectedResolution={selectedResolution}
							dispatchRenderImage={dispatchRenderImage}
						/>
					</Grid>

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<Grid container>
								<Grid item xs={9}>
									<Typography
										id="discrete-slider"
										gutterBottom
									>
										{t('Number of zones')}
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Slider
										value={zones}
										onChange={handleZonesChange}
										defaultValue={zones}
										aria-labelledby="discrete-slider"
										valueLabelDisplay="auto"
										step={1}
										marks
										min={1}
										max={10}
									/>
								</Grid>
							</Grid>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<Grid container>
								<Grid item xs={8}></Grid>
								<Grid item xs={4} spacing={0}>
									<Paper elevation={3}>
										<Typography
											component="div"
											variant="body"
										>
											<Grid
												component="label"
												container
												alignItems="center"
												spacing={1}
											>
												<Grid
													item
													className={
														classes.fixGridSwitch
													}
												>
													l/ha
												</Grid>
												<Grid item>
													<AntSwitch
														defaultChecked
														onChange={(e) =>
															handleMetricChange(
																e
															)
														}
													/>
												</Grid>
												<Grid item>kg/ha</Grid>
											</Grid>
										</Typography>
									</Paper>
								</Grid>
							</Grid>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<Grid container>
								<Grid item container>
									<Grid item xs={4}></Grid>
									<Grid item xs={2}>
										<Typography variant="subtitle2">
											NDVI
										</Typography>
									</Grid>
									<Grid item xs={2}>
										<Typography variant="subtitle2">
											{t('hectares')}
										</Typography>
									</Grid>

									<Grid item xs={4}>
										<Typography variant="subtitle2">
											{t('Rate')}
										</Typography>
									</Grid>
								</Grid>

								{zonedGeom &&
									zonedGeom.features
										.sort((a, b) =>
											a.properties.mean >
											b.properties.mean
												? 1
												: b.properties.mean >
												  a.properties.mean
												? -1
												: 0
										)
										.map((geom, index) => (
											<Grid key={`${index}_${geom.properties.area}`} item container>
												<Grid item xs={4}>
													<Typography
														id="discrete-slider"
														gutterBottom
													>
														{t('zone')} {geom.id}
													</Typography>
												</Grid>
												<Grid item xs={2}>
													<Chip
														label={geom.properties.mean?.toFixed(
															2
														)}
														style={{
															backgroundColor:
																findClosestLabel(
																	zoneColorLabels,
																	geom
																		.properties
																		?.mean
																)?.color,
														}}
													/>
												</Grid>
												<Grid item xs={2}>
													<Typography
														id="discrete-slider"
														gutterBottom
													>
														{geom.properties.area?.toFixed(
															2
														)}{' '}
														(
														{(
															(geom.properties.area?.toFixed(
																2
															) *
																100) /
															zonedGeom.features.reduce(
																(a, c) =>
																	a +
																	c.properties
																		.area,
																0
															)
														).toFixed(0)}
														%)
													</Typography>
												</Grid>

												<Grid item xs={4}>
													<TextField
														id={`harvested_qty-${index}`}
														name="harvested_qty"
														type="number"
														fullWidth
														error={errorRate}
														size="small"
														value={
															geom.properties.rate
														}
														onChange={(e) =>
															setZonedGeom({
																type: 'rate',
																value: {
																	zone: geom.id,
																	rate: e
																		.target
																		.value,
																},
															})
														}
														InputProps={{
															endAdornment: (
																<InputAdornment position="start">
																	{metric}/ha
																</InputAdornment>
															),
														}}
													/>
												</Grid>
											</Grid>
										))}

								<Grid item container style={{ marginTop: 5 }}>
									<Grid item xs={8}>
										<Typography variant="h6">
											{t('Total quantity')}
										</Typography>
									</Grid>

									<Grid item xs={4}>
										<Typography variant="h6">
											{zonedGeom &&
												zonedGeom.features
													.reduce(
														(a, geom) =>
															geom.properties
																.area *
																geom.properties
																	.rate +
															a,
														0
													)
													?.toFixed(2)}{' '}
											{metric}
										</Typography>
										<Typography variant="subtitle1">
											{t('fertilizers')}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'end' : 'center'
							}
						>
							<Autocomplete
								id="combo-box-demo"
								value={boderComputer}
								onChange={handleSelectedFormat}
								options={borderComputerTypes}
								getOptionLabel={(option) => option.name}
								style={{ width: '100%' }}
								disableClearable
								renderInput={(params) => (
									<TextField
										{...params}
										label={t('Type of computer on board')}
										size="small"
									/>
								)}
							/>
						</Box>
					</Grid>
					<Grid item xs={12} container spacing={2}>
						{(isClientPaying() || loggedUser.is_superuser) &&
							<Grid item xs={12}>
								<Box
									display="flex"
									justifyContent={
										isWidthUp('sm', width)
											? 'end'
											: 'center'
									}
								>
									<Button
										id="download-prescription-map-archive"
										variant="outlined"
										color="primary"
										style={{ width: '100%' }}
										endIcon={<CloudDownloadIcon />}
										onClick={createShapefile}
										label="Download file"
										farms={farms}
									>
										{t('Download file')}
									</Button>
								</Box>
							</Grid>
						}
						<Grid item xs={12}>
							{boderComputer.name === 'John Deere' && (
								<Box
									display="flex"
									justifyContent={
										isWidthUp('sm', width)
											? 'end'
											: 'center'
									}
								>
									<GraniotPremiumButton
										variant="outlined"
										color="primary"
										style={{ width: '100%' }}
										action={uploadToJohnDeereOC}
										label="upload_to_johndeere_operation_center"
										farms={farms}
									/>
								</Box>
							)}
						</Grid>

						<Grid item xs={12}>
							{boderComputer.name === 'John Deere' && (
								<>
									{transferToJohnDeereOC.isSuccess && (
										<Alert
											onClose={() => {
												transferToJohnDeereOC.reset()
											}}
											severity={
												transferToJohnDeereOC.data
													.status
											}
										>
											{t(
												transferToJohnDeereOC.data
													.message
											)}
										</Alert>
									)}
								</>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12} sm={6}>
				<Paper>
					{isLoading ? (
						<Box display="flex" justifyContent="center">
							<CircularProgress className={classes.loading} />
						</Box>
					) : (
						<FertilizerMapKmean
							parcel={parcel}
							imagesDate={imagesDate}
							map={baseMap}
							setMap={setBaseMap}
							layer={selectedLayer}
							selectedResolution={selectedResolution}
							zones={zones}
							setZonedGeom={setZonedGeom}
							maxCC={maxCC}
						/>
					)}
				</Paper>
			</Grid>
		</Grid>
	)
}

export default withWidth()(FertilizerVegetation)
